import React, { useState } from 'react';
import Popover from '@material-ui/core/Popover';
import { BsTags } from 'react-icons/bs';
import { INK, FONT_MONO } from 'Utils/styleTokens';

// c2-manager returns provider tags (AWS tags, Azure resource tags, GCP labels) on each
// landscape row as a key → value map under `tags`, omitted entirely when the resource has
// none. Insertion order is the provider's order, so keep it rather than sorting.
export const tagEntries = (tags) => {
  if (!tags || typeof tags !== 'object' || Array.isArray(tags)) return [];
  return Object.entries(tags).filter(([key]) => key !== null && key !== undefined && key !== '');
};

// Compact "N tags" pill for a Cloud Landscape row. Clicking it opens a popover listing
// the key/value pairs. The pill sits inside a row whose onClick toggles the port-exposure
// drawer, so every click here (pill, popover body, backdrop) stops propagation — the
// popover renders in a portal but React still bubbles its events through the row.
const TagsPill = ({ tags, resourceLabel }) => {
  const entries = tagEntries(tags);
  const [anchor, setAnchor] = useState(null);
  if (entries.length === 0) return null;
  const open = Boolean(anchor);
  const count = entries.length;
  const summary = `${count} tag${count === 1 ? '' : 's'}`;

  return (
    <>
      <button
        type='button'
        title='Show provider tags'
        aria-label={`${summary} — show provider tags`}
        aria-haspopup='dialog'
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.05em',
          padding: '3px 8px',
          borderRadius: 999,
          border: `1px solid ${open ? INK[300] : INK[200]}`,
          background: open ? INK[150] : INK[100],
          color: INK[500],
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          lineHeight: 1.2,
        }}
      >
        <BsTags size={10} />
        {summary}
      </button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { borderRadius: 10, boxShadow: '0 6px 24px rgba(33,38,48,.16)' } }}
      >
        <div
          role='dialog'
          aria-label={`Tags${resourceLabel ? ` for ${resourceLabel}` : ''}`}
          style={{ padding: '12px 14px', minWidth: 240, maxWidth: 440, maxHeight: 320, overflow: 'auto' }}
        >
          <div
            className='sg-eyebrow'
            style={{
              fontSize: 10.5,
              color: INK[400],
              marginBottom: 8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={resourceLabel || ''}
          >
            {summary}
            {resourceLabel ? ` · ${resourceLabel}` : ''}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(72px, max-content) minmax(0, 1fr)',
              columnGap: 14,
              rowGap: 5,
              fontSize: 12,
            }}
          >
            {entries.map(([key, value]) => {
              const text = value === null || value === undefined || value === '' ? '—' : String(value);
              return (
                <React.Fragment key={key}>
                  <div
                    style={{ fontFamily: FONT_MONO, color: INK[500], wordBreak: 'break-all' }}
                    title={key}
                  >
                    {key}
                  </div>
                  <div
                    style={{ fontFamily: FONT_MONO, color: INK[900], wordBreak: 'break-all' }}
                    title={text}
                  >
                    {text}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default TagsPill;
